import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import { NotificationManager } from '../../components/notification-manager';
import { Images } from '../../components/shared-game-components/game-images';
import { PageTopBar } from '../../components/page-top-bar';
import { SaveButton, DeleteButton } from '../../components/action-buttons';
import { useAuthStore, useCareerStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { useNavigation } from '../../utils/use-navigation';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { CareerForm, UpsertCareerCard } from '../../stores/career';
import { Card, Switch } from 'antd';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

const YupForm = yup.object({
    image: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    active: yup.boolean().required(),
});

export const CareerPage = observer(() => {
    const params = useParams<Params>();
    const store = useCareerStore();

    const authStore = useAuthStore();
    const navigation = useNavigation();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const formik = useFormik<CareerForm>({
        initialValues: {
            active: false,
            image: {
                fr: '',
                en: '',
            },
        },
        validateOnMount: true,
        validationSchema: YupForm,
        onSubmit: () => void 0,
    });

    const saveCareer = useCallback(async () => {
        const payload = YupForm.validateSync(formik.values, {
            stripUnknown: true,
        });
        if (params.action === 'edit') {
            await store.updateCareer(params.id!, payload as UpsertCareerCard);
        } else {
            await store.createCareer(payload as UpsertCareerCard);
        }
    }, [formik.values, params.action, params.id, store]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: saveCareer,
    });

    const createCareer = useCallback(async () => {
        await saveCareer();
        clearDirty(() => navigation.goToCareers());
    }, [saveCareer, clearDirty, navigation]);

    const updateCareer = useCallback(async () => {
        await saveCareer();
        clearDirty(() => navigation.goToCareers());
    }, [saveCareer, clearDirty, navigation]);

    if (!YupParams.isValidSync(params)) {
        navigation.goToHome();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            store
                .fetchCareer(params.id!)
                .then((career) => {
                    if (career) {
                        formik.resetForm({ values: career });
                    } else {
                        throw new Error('Career not found');
                    }
                })
                .catch(() => navigation.goToHome());
        }
    }, []);

    const onDeleteConfirm = async () => {
        if (params.id) store.deleteCareer(params.id);
        else NotificationManager.showError("Can't delete this career");
        navigation.goToCareers();
    };

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createCareer}
                        loading={store.isUpdatedCareer}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={store.isDeletingCareer}
                        />
                        <SaveButton
                            onClick={updateCareer}
                            loading={store.isUpdatedCareer}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const transformId = params.id ? Number(params.id) : undefined;

    return (
        <>
            <PageTopBar
                title={
                    transformId && transformId !== 0
                        ? `Career #${transformId}`
                        : 'New Career'
                }
                goBack={{
                    callback: () => navigation.goToCareers(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <div className={'content_with_margins'}>
                <Card>
                    <div>
                        <span className="left_label">Active</span>
                        <Switch
                            checked={formik.values.active}
                            onChange={(e) => formik.setFieldValue('active', e)}
                            defaultChecked
                        />
                    </div>

                    <div>
                        <Images
                            image={formik.values.image}
                            title="Image"
                            applyStyle={false}
                            onChange={(image) => {
                                formik.setValues({
                                    ...formik.values,
                                    image,
                                });
                            }}
                            onDelete={(image) => {
                                formik.setValues({
                                    ...formik.values,
                                    image,
                                });
                            }}
                        />
                    </div>
                </Card>
            </div>
        </>
    );
});
